import { Wemolon } from '@wemolo/components/icons/system'
import { DateTime } from 'luxon'
import React, { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useIsSidebarCollapsed, useSidebarStore } from '../../store/useSidebarStore'
import { useAuth0 } from '../../utils/auth'
import { useGlobalSearchPageStore } from '../../views/GlobalSearch/GlobalSearchPage'
import { WemoloLogo, WemoloTypography } from '../Wemolo'
import styles from './Sidebar.module.css'

export type SidebarNavItem = {
    title: React.ReactNode
    itemId: string
    subNav?: SidebarNavItem[]
}

export type SidebarProps = { navItems: SidebarNavItem[] | undefined }

const cx = (...classes: Array<string | false | undefined>) => classes.filter(Boolean).join(' ')

export const Sidebar = ({ navItems }: SidebarProps) => {
    const location = useLocation()
    const { logout } = useAuth0()
    const { setIsOpen } = useGlobalSearchPageStore()
    const isCollapsed = useIsSidebarCollapsed()
    const isCompact = useSidebarStore((state) => state.isCompact)
    const toggle = useSidebarStore((state) => state.toggle)

    const [selectedPath, setSelectedPath] = useState(location.pathname)
    useEffect(() => {
        setSelectedPath(location.pathname)
    }, [location.pathname])

    // Items that perform an action instead of navigating to a route (modal / external link / logout).
    const ACTION_ITEMS = new Set(['/globalSearch', 'feedback', 'logout'])

    const handleAction = (itemId: string) => {
        if (itemId === 'logout') {
            logout()
            return
        }
        if (itemId === '/globalSearch') {
            setIsOpen(true)
            return
        }
        if (itemId === 'feedback') {
            window.open('https://tally.so/r/mK5GBX', '_blank')
        }
    }

    const renderItem = (item: SidebarNavItem, isSubItem = false) => {
        const className = cx(styles.navItem, isSubItem && styles.subItem, selectedPath === item.itemId && styles.active)

        // Real routes render as links so Cmd/Ctrl/middle-click opens them in a new tab.
        if (ACTION_ITEMS.has(item.itemId)) {
            return (
                <button key={item.itemId} type="button" className={className} onClick={() => handleAction(item.itemId)}>
                    {item.title}
                </button>
            )
        }

        return (
            <Link key={item.itemId} to={item.itemId} className={className}>
                {item.title}
            </Link>
        )
    }

    return (
        <div className={cx(styles.sidebar, isCollapsed && styles.collapsed)}>
            <div className={styles.topSection}>
                {isCollapsed ? (
                    <span className={styles.logoMark}>
                        <Wemolon />
                    </span>
                ) : (
                    <div className={styles.logo}>
                        <WemoloLogo $dark={true} width="108" height="25" />
                    </div>
                )}
            </div>

            <nav className={styles.nav}>
                {navItems?.map((item) => (
                    <React.Fragment key={item.itemId}>
                        {renderItem(item)}
                        {item.subNav?.map((subItem) => renderItem(subItem, true))}
                    </React.Fragment>
                ))}
            </nav>

            <div className={styles.bottomSection}>
                <WemoloTypography variant="body-small" tag="span">
                    © {DateTime.local().year} Wemolo GmbH
                </WemoloTypography>
                <WemoloTypography variant="body-small" tag="span">
                    Made with ♡ from AIoT
                </WemoloTypography>
                {/* @ts-ignore -- __APP_VERSION__ is injected by Vite define at build time */}
                <WemoloTypography variant="body-small" tag="span">
                    Version: {__APP_VERSION__}
                </WemoloTypography>
            </div>

            {/* The rail is locked to collapsed on compact viewports, so the toggle only shows on desktop. */}
            {!isCompact && (
                <button
                    type="button"
                    className={styles.collapseBar}
                    onClick={toggle}
                    aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    aria-pressed={isCollapsed}
                >
                    <span className={styles.collapseHandle} />
                </button>
            )}
        </div>
    )
}
